import type { ResizablePanelHandle } from "@cohesive/ui-components";
import { ResizablePanel } from "@cohesive/ui-components";
import { AnimatePresence, motion } from "motion/react";
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

import { cn } from "@/utils";

import { usePaneLayoutWidths } from "@/context/PaneLayoutWidthsContext";
import { useRightPaneExpandContext } from "@/context/RightPaneExpandContext";
import type { LocationState } from "@/types/pane-routing";
import { useStore } from "@xyflow/react";
import {
  CANVAS_FLOATING_CHAT_TOOLBAR_RIGHT_MARGIN,
  CANVAS_TOP_CONTROLS_BAND_BOTTOM_PX,
  useCanvasFloatingChat,
} from "../context/CanvasFloatingChatContext";
import { useCanvasOnboardingState } from "../context/CanvasOnboardingContext";
import { useCanvasRoute } from "../context/CanvasRouteContext";
import { useCanvasUi } from "../context/CanvasUiContext";
import { useCanvasChatReferencedEntities } from "../hooks/useCanvasChatReferencedEntities";
import { useCanvasComposerProgress } from "../hooks/useCanvasComposerProgress";
import { useCanvasWidth } from "../hooks/useCanvasWidth";
import { CanvasSessionPaneContent } from "./CanvasSessionPane";
import { CanvasChatFab } from "./composer/CanvasChatFab";
import { CanvasLanding } from "./composer/CanvasLanding";

const ChatResizablePanel = ResizablePanel as React.ForwardRefExoticComponent<
  React.ComponentProps<typeof ResizablePanel> &
    React.RefAttributes<ResizablePanelHandle>
>;

/** Matches shadcn `PopoverContent` (tailwind `animate-in` / `zoom-in-95` / `slide-in-from-*-2`). */
const FLOATING_CHAT_MOTION_TRANSITION = {
  duration: 0.2,
  ease: [0.16, 1, 0.3, 1] as const,
};

const FLOATING_CHAT_MOTION_INITIAL = {
  opacity: 0,
  scale: 0.95,
  x: 8,
  y: 8,
};

const FLOATING_CHAT_MIN_HEIGHT = 400;

/**
 * Fixed width the chat takes when it docks to the LEFT beside a full-width
 * detail pane (two-pane layout). Matches the staging dashboard-comp
 * `CHAT_EXPANDED_W` (= `--bin-w` * 2 - `--space-12`).
 */
const CANVAS_CHAT_DOCK_WIDTH_PX = 512;

/** FAB diameter (`size-14`) — the toolbar only needs to clear this while closed. */
const FAB_FOOTPRINT_PX = 56;

/** Total vertical inset so the panel stays inside the React Flow shell (not under chrome). */
const REACT_FLOW_CHAT_VERTICAL_INSET = 16;

/**
 * Horizontal clearance vs the React Flow wrapper width: matches shell `right-2`
 * (8px) plus the same gutter on the left when the panel is dragged to max width.
 */
const FLOATING_CHAT_HORIZONTAL_INSET_PX = 16;

/** Extra horizontal clearance when only the right pane is open (toolbar / shell margin). */
const FLOATING_CHAT_RIGHT_DOCK_ONLY_MARGIN_PX = 100;

/** Matches `@max-[399px]:fixed` on the shell (container query on React Flow wrapper). */
const CANVAS_FLOATING_CHAT_FIXED_MODE_MAX_CONTAINER_PX = 399;

const CORNER_SHELL_CLASS = cn(
  "absolute right-2 bottom-2 z-30 origin-bottom-right",
  // React Flow shell is `@container` on CanvasPage; below 400px use fixed so the chat clears the right pane.
  "@max-[399px]:fixed @max-[399px]:z-50",
);

/**
 * Full-height dock: flush to the right edge and to the shell's top + bottom
 * (`inset-y-0`, no vertical inset) so it spans the full canvas height and lines
 * up with the detail/right pane. A definite-height `flex` wrapper is what makes
 * the inner panel fill vertically — the same trick the RightPane dock relies on
 * (ResizablePanel only manages height when a vertical handle exists, so here the
 * height comes from the flex parent + `h-full` on the panel, not the panel).
 */
const DOCKED_SHELL_CLASS = cn(
  "absolute right-0 inset-y-0 z-30 flex origin-right",
  "@max-[399px]:fixed @max-[399px]:z-50",
);

function fallbackMaxPanelHeightPx() {
  if (typeof window === "undefined") return 560;
  return Math.max(
    FLOATING_CHAT_MIN_HEIGHT,
    Math.floor(window.innerHeight - 80),
  );
}

function maxPanelHeightFromFlowWrapper(el: HTMLDivElement | null) {
  if (!el) return fallbackMaxPanelHeightPx();
  return Math.max(
    FLOATING_CHAT_MIN_HEIGHT,
    Math.floor(el.clientHeight - REACT_FLOW_CHAT_VERTICAL_INSET),
  );
}

function chatShellOverlapsTopControls(
  chatShell: HTMLElement,
  flowWrapper: HTMLDivElement | null,
): boolean {
  if (!flowWrapper) return false;
  const chatTop = chatShell.getBoundingClientRect().top;
  const controlsBottom =
    flowWrapper.getBoundingClientRect().top +
    CANVAS_TOP_CONTROLS_BAND_BOTTOM_PX;
  return chatTop <= controlsBottom;
}

export function CanvasFloatingChatPanel() {
  const { canvasId } = useCanvasRoute();
  const canvasWidthPx = useCanvasWidth();
  const { reactFlowWrapperEl } = useCanvasUi();
  // Node COUNT only (selector) — so node drags/streams don't re-render the chat window.
  const nodeCount = useStore((s) => s.nodes.length);
  const { activeOnboarding, highlightTargetId } = useCanvasOnboardingState();
  const {
    currentSessionId,
    canGoBack,
    goBack,
    openSession,
    setBottomToolbarRightClearancePx,
    setTopControlsRightClearancePx,
    open,
    expand,
    collapse,
    sizeClass,
    toggleSizeClass,
    activeSessionId,
    hasStarted,
  } = useCanvasFloatingChat();
  const expandedShellRef = useRef<HTMLDivElement>(null);
  const [flowShellMaxHeightPx, setFlowShellMaxHeightPx] = useState(
    fallbackMaxPanelHeightPx,
  );

  // Shared chat data (single source for the floating overlay AND the input). Canvas-scoped,
  // so it's safe at the panel level (no SessionProvider needed).
  const { referencedEntities, onRemoveEntity, clearTextSelectionsFromMessage } =
    useCanvasChatReferencedEntities(canvasId ?? "");
  const progress = useCanvasComposerProgress(
    currentSessionId ?? activeSessionId,
  );

  const isFloatingChatFixedToViewport =
    canvasWidthPx > 0 &&
    canvasWidthPx <= CANVAS_FLOATING_CHAT_FIXED_MODE_MAX_CONTAINER_PX;

  const [viewportWidthPx, setViewportWidthPx] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 2000,
  );
  const [viewportHeightPx, setViewportHeightPx] = useState(() =>
    typeof window !== "undefined" ? window.innerHeight : 1000,
  );

  useEffect(() => {
    const onResize = () => {
      setViewportWidthPx(window.innerWidth);
      setViewportHeightPx(window.innerHeight);
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Geometry of the canvas content area. Its left/top/height stay valid even
  // when the React Flow wrapper is squeezed to ~0 width by a full-width detail
  // pane — so the left-docked chat / FAB can anchor to the content area (after
  // the left explorer, below any chrome) without hard-coding a header offset.
  const [wrapperRect, setWrapperRect] = useState<DOMRect | null>(null);
  useLayoutEffect(() => {
    const el = reactFlowWrapperEl;
    if (!el) return;
    const measure = () => setWrapperRect(el.getBoundingClientRect());
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [reactFlowWrapperEl]);

  const { rightPanelWidth } = usePaneLayoutWidths();
  const { isExpanded: isRightPanelExpanded, setLeftChatDockPx } =
    useRightPaneExpandContext();
  const location = useLocation();

  const isRightPanelOpenByRoute = useMemo(() => {
    const state = (location.state as LocationState) || {};
    return Boolean(state.rightPane?.pathname);
  }, [location.state]);

  // ── Surface state ─────────────────────────────────────────────────────────
  // open = the chat window is up; otherwise the FAB launcher (or the centered
  // landing on a fresh canvas). `full-height` docks the window flush-right.
  const isOpen = open && !!currentSessionId;
  const isFullHeight = sizeClass === "full-height";
  const showLanding = !hasStarted && nodeCount === 0;
  // During onboarding the coach card owns the bottom of the screen. Hide the
  // FAB + landing until a step spotlights the chat launcher or onboarding's
  // dedicated session is already open (steps 5 and 7 teach "open chat" via
  // `agent-chat-toggle` on the FAB, not a toolbar button).
  const onboardingSuppressed =
    !!activeOnboarding && !isOpen && highlightTargetId !== "agent-chat-toggle";

  // When the detail pane is expanded full-width it occupies the right, so the
  // chat moves to the LEFT: the FAB and the windowed window float over the
  // detail; full-height instead docks as a fixed-width column and the detail
  // shrinks to sit beside it (two-pane). `isLeftDocked` is what reserves space.
  const dockLeft = isRightPanelExpanded && isRightPanelOpenByRoute;
  const isLeftDocked = dockLeft && isOpen && isFullHeight;

  useEffect(() => {
    setLeftChatDockPx(isLeftDocked ? CANVAS_CHAT_DOCK_WIDTH_PX : 0);
    return () => setLeftChatDockPx(0);
  }, [isLeftDocked, setLeftChatDockPx]);

  // Windowed chat floating over a full-width detail: keep it from covering the
  // whole pane.
  const leftWindowMaxWidthPx = Math.max(400, Math.floor(viewportWidthPx * 0.5));

  const maxWidthPx = useMemo(() => {
    const viewport = viewportWidthPx;
    const gutterPx = FLOATING_CHAT_HORIZONTAL_INSET_PX;
    const rightOpen = isRightPanelOpenByRoute;
    const rightPx = rightOpen ? rightPanelWidth : 0;

    let rawMax: number;

    if (rightOpen && isRightPanelExpanded) {
      rawMax = viewport;
    } else if (
      rightOpen &&
      !isRightPanelExpanded &&
      isFloatingChatFixedToViewport
    ) {
      rawMax = viewport - gutterPx;
    } else if (rightOpen && !isRightPanelExpanded) {
      rawMax = viewport - rightPx - FLOATING_CHAT_RIGHT_DOCK_ONLY_MARGIN_PX;
    } else if (isFloatingChatFixedToViewport) {
      rawMax = viewport;
    } else {
      rawMax = Math.floor(viewport * 0.6);
    }

    return rawMax;
  }, [
    isFloatingChatFixedToViewport,
    isRightPanelExpanded,
    isRightPanelOpenByRoute,
    rightPanelWidth,
    viewportWidthPx,
  ]);

  useLayoutEffect(() => {
    const el = reactFlowWrapperEl;
    const update = () => {
      setFlowShellMaxHeightPx(maxPanelHeightFromFlowWrapper(el));
    };
    update();
    if (!el) return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [reactFlowWrapperEl]);

  const { initialHeight, minHeight } = useMemo(() => {
    if (typeof window === "undefined") {
      return { initialHeight: 520, minHeight: FLOATING_CHAT_MIN_HEIGHT };
    }
    const cap = flowShellMaxHeightPx;
    const comfortable = Math.min(560, cap);
    return {
      minHeight: FLOATING_CHAT_MIN_HEIGHT,
      initialHeight: Math.max(FLOATING_CHAT_MIN_HEIGHT, comfortable),
    };
  }, [flowShellMaxHeightPx]);

  // Toolbar clearance: keep the bottom canvas toolbar left of the chat. While closed it only
  // needs to clear the FAB footprint; while open the window is observed (resizable). When the
  // shell grows upward (full-height dock or manual vertical resize), also offset the top-right
  // share + controls row. Landing is centered and onboarding-suppressed state has no shell.
  useLayoutEffect(() => {
    const resetClearances = () => {
      setBottomToolbarRightClearancePx(0);
      setTopControlsRightClearancePx(0);
    };

    if (!canvasId || onboardingSuppressed || showLanding) {
      resetClearances();
      return;
    }
    // Detail pane full-width: the chat/FAB live on the LEFT, so the bottom
    // toolbar needs no right clearance for them.
    if (dockLeft) {
      resetClearances();
      return;
    }

    const applyClearances = (width: number, overlapsTopControls: boolean) => {
      const bottomClearance =
        Math.ceil(width) + CANVAS_FLOATING_CHAT_TOOLBAR_RIGHT_MARGIN;
      setBottomToolbarRightClearancePx(bottomClearance);
      setTopControlsRightClearancePx(overlapsTopControls ? bottomClearance : 0);
    };

    if (!isOpen) {
      applyClearances(FAB_FOOTPRINT_PX, false);
      return;
    }

    let ro: ResizeObserver | undefined;
    let measure: (() => void) | undefined;
    let rafId = 0;
    let cancelled = false;

    const cleanup = () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      ro?.disconnect();
      if (measure) {
        window.removeEventListener("resize", measure);
      }
    };

    const attach = (el: HTMLDivElement) => {
      measure = () => {
        const rect = el.getBoundingClientRect();
        const overlapsTopControls =
          isFullHeight || chatShellOverlapsTopControls(el, reactFlowWrapperEl);
        applyClearances(rect.width, overlapsTopControls);
      };

      ro = new ResizeObserver(() => {
        measure?.();
      });
      ro.observe(el);
      if (reactFlowWrapperEl) {
        ro.observe(reactFlowWrapperEl);
      }
      window.addEventListener("resize", measure);
      measure();
    };

    const tryAttach = () => {
      if (cancelled) return;
      const el = expandedShellRef.current;
      if (el) {
        attach(el);
        return;
      }
      // AnimatePresence / motion can commit the shell one frame after isOpen flips.
      rafId = requestAnimationFrame(tryAttach);
    };

    tryAttach();
    return cleanup;
  }, [
    canvasId,
    isOpen,
    isFullHeight,
    showLanding,
    onboardingSuppressed,
    dockLeft,
    reactFlowWrapperEl,
    setBottomToolbarRightClearancePx,
    setTopControlsRightClearancePx,
  ]);

  // The window does NOT auto-minimize — minimizing to the FAB is an explicit action (header Minus).

  // ── Render (all hooks above) ──────────────────────────────────────────────
  if (!canvasId) return null;
  if (onboardingSuppressed) return null;
  if (showLanding) return <CanvasLanding />;

  // ── Left side: the detail pane is expanded full-width ─────────────────────
  // The chat moves left of the detail. Geometry is fixed (computed from the
  // canvas content rect) because the React Flow wrapper is squeezed to ~0 width.
  if (isOpen && dockLeft && wrapperRect) {
    if (isFullHeight) {
      // Docked fixed-width column; the detail shrinks beside it (the
      // `setLeftChatDockPx` reserve above frees exactly this strip).
      return (
        <AnimatePresence initial={false}>
          <motion.div
            key="left-dock"
            ref={expandedShellRef}
            className="fixed z-50 flex overflow-hidden border-r border-n-10 shadow-[var(--overlay-edge-shadow)]"
            style={{
              left: wrapperRect.left,
              top: wrapperRect.top,
              height: wrapperRect.height,
              width: CANVAS_CHAT_DOCK_WIDTH_PX,
            }}
            initial={FLOATING_CHAT_MOTION_INITIAL}
            animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
            exit={FLOATING_CHAT_MOTION_INITIAL}
            transition={FLOATING_CHAT_MOTION_TRANSITION}
          >
            <div className="h-full w-full">
              <CanvasSessionPaneContent
                canvasId={canvasId}
                flowSessionId={currentSessionId ?? ""}
                canGoBack={canGoBack}
                onGoBack={goBack}
                onClose={collapse}
                isChatExpanded
                onToggleExpand={toggleSizeClass}
                onOpenSession={openSession}
                referencedEntities={referencedEntities}
                onRemoveEntity={onRemoveEntity}
                clearTextSelectionsFromMessage={clearTextSelectionsFromMessage}
              />
            </div>
          </motion.div>
        </AnimatePresence>
      );
    }
    // Windowed: float in the bottom-left over the full-width detail.
    return (
      <AnimatePresence initial={false}>
        <motion.div
          key="left-window"
          ref={expandedShellRef}
          className="fixed z-50 origin-bottom-left"
          style={{
            left: wrapperRect.left + 8,
            bottom: viewportHeightPx - wrapperRect.bottom + 8,
          }}
          initial={FLOATING_CHAT_MOTION_INITIAL}
          animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
          exit={FLOATING_CHAT_MOTION_INITIAL}
          transition={FLOATING_CHAT_MOTION_TRANSITION}
        >
          <ChatResizablePanel
            key={`left-${sizeClass}`}
            // Resizable floating window with the drag pills hidden
            // (`showHandleIndicator={false}`); `directions` drives both the
            // resize edges (right/top + corner) and its height management.
            directions={["right", "top", "top-right"]}
            showHandleIndicator={false}
            isClosed={false}
            transitionDuration={0}
            minWidth={400}
            maxWidth={leftWindowMaxWidthPx}
            initialWidth={510}
            initialHeight={initialHeight}
            minHeight={minHeight}
            maxHeight={flowShellMaxHeightPx}
            storageKey="canvas-floating-chat-width"
            className="relative overflow-hidden rounded-xl border border-n-10 shadow-elev-3"
          >
            <CanvasSessionPaneContent
              canvasId={canvasId}
              flowSessionId={currentSessionId ?? ""}
              canGoBack={canGoBack}
              onGoBack={goBack}
              onClose={collapse}
              isChatExpanded={false}
              onToggleExpand={toggleSizeClass}
              onOpenSession={openSession}
              referencedEntities={referencedEntities}
              onRemoveEntity={onRemoveEntity}
              clearTextSelectionsFromMessage={clearTextSelectionsFromMessage}
            />
          </ChatResizablePanel>
        </motion.div>
      </AnimatePresence>
    );
  }

  if (isOpen) {
    return (
      <AnimatePresence initial={false}>
        <motion.div
          key="window"
          ref={expandedShellRef}
          // Windowed floats in the bottom-right corner; full-height docks flush
          // to the right edge and fills the shell height (definite-height flex).
          className={isFullHeight ? DOCKED_SHELL_CLASS : CORNER_SHELL_CLASS}
          initial={FLOATING_CHAT_MOTION_INITIAL}
          animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
          exit={FLOATING_CHAT_MOTION_INITIAL}
          transition={FLOATING_CHAT_MOTION_TRANSITION}
        >
          <ChatResizablePanel
            // Remount across size classes: ResizablePanel manages height only
            // when a vertical handle exists, and leaves a stale inline height
            // behind when the handle set changes — a fresh instance avoids that.
            // `transitionDuration={0}` keeps the swap instant ("snap").
            key={sizeClass}
            // Full-height: width-only resize (left edge). Windowed stays
            // resizable on its left/top edges + top-left corner but hides the
            // drag pills (`showHandleIndicator` below) so the floating window
            // reads clean; `directions` also drives its height management.
            directions={isFullHeight ? ["left"] : ["left", "top", "top-left"]}
            isClosed={false}
            transitionDuration={0}
            minWidth={400}
            maxWidth={maxWidthPx}
            initialWidth={510}
            initialHeight={isFullHeight ? undefined : initialHeight}
            minHeight={isFullHeight ? undefined : minHeight}
            maxHeight={isFullHeight ? undefined : flowShellMaxHeightPx}
            storageKey="canvas-floating-chat-width"
            // Show the left-edge drag pill while docked (like the RightPane).
            showHandleIndicator={isFullHeight}
            className={cn(
              "relative",
              isFullHeight
                ? // Docked: fill the flex shell (h-full), square the right edge,
                  // drop the border, lift with a left-only edge shadow. No
                  // `overflow-hidden` here — the left-edge handle pill sits just
                  // outside the panel edge and would be clipped (the inner
                  // content already clips itself).
                  "h-full rounded-none border-0 shadow-[var(--overlay-edge-shadow)]"
                : // Floating panel chrome: rounded-xl + hairline edge + elev-3.
                  // `overflow-hidden` clips the rounded corners; the resize
                  // grab zones are invisible (handles hidden) so nothing visible
                  // is lost — the 2px that overhangs the edge is just unused.
                  "overflow-hidden rounded-xl border border-n-10 shadow-elev-3",
            )}
          >
            <CanvasSessionPaneContent
              canvasId={canvasId}
              flowSessionId={currentSessionId ?? ""}
              canGoBack={canGoBack}
              onGoBack={goBack}
              onClose={collapse}
              isChatExpanded={isFullHeight}
              onToggleExpand={toggleSizeClass}
              onOpenSession={openSession}
              referencedEntities={referencedEntities}
              onRemoveEntity={onRemoveEntity}
              clearTextSelectionsFromMessage={clearTextSelectionsFromMessage}
            />
          </ChatResizablePanel>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Closed: the FAB launcher (activity pulse + attached-entity count badge).
  // While a full-width detail pane occupies the right, the FAB pins to the
  // bottom-LEFT of the canvas content area instead of its shell bottom-right.
  const handleFabOpen = () => {
    if (activeOnboarding?.openAgentChat) {
      activeOnboarding.openAgentChat();
      return;
    }
    expand();
  };

  return (
    <CanvasChatFab
      onOpen={handleFabOpen}
      running={progress.isRunning}
      dockStyle={
        dockLeft && wrapperRect
          ? {
              left: wrapperRect.left + 16,
              bottom: viewportHeightPx - wrapperRect.bottom + 16,
            }
          : undefined
      }
    />
  );
}
